import {
  CheckoutBasket,
  CheckoutBasketItem,
  ContractType,
  convertPaymentOptionsToObject,
} from '@common/util-models';
import { createSelector } from '@ngrx/store';
import {
  SkyBasketItem,
  SkyBasketItemApplianceDetails,
  SkyBasketItemQuoteDetails,
} from '@sky/util-models';
import { BasketState } from '../basket.reducer';
import { getBasketState } from '../basket.selectors';
import { QuotesState } from './quotes.reducer';

export const getQuotesState = createSelector(
  getBasketState,
  (state: BasketState) => state.quotes
);

export const getQuotesLoaded = createSelector(
  getQuotesState,
  (state: QuotesState) => state.remoteState
);

export const getBasketItemQuoteApiResult = createSelector(
  getQuotesState,
  (state: QuotesState) => state.quoteApiResult?.basket?.items[0]
);

export const getPurchasePrice = createSelector(
  getBasketState,
  (state: BasketState) => {
    const [firstKey] = Object.keys(state.devices.entities);
    return state.devices.entities[firstKey]?.purchasePrice;
  }
);

export const getAllQuotes = createSelector(
  getBasketItemQuoteApiResult,
  (basketItemQuoteApiResult: SkyBasketItem | undefined) =>
    basketItemQuoteApiResult?.data.quotes
);

export const getLtMaxItems = createSelector(
  getBasketItemQuoteApiResult,
  (basketItemQuoteApiResult: SkyBasketItem | undefined) =>
    basketItemQuoteApiResult?.data.applianceDetails.ltMaxItems
);

export const getSelectedQuote = createSelector(
  getQuotesState,
  (state: QuotesState) => state.selectedQuote
);

export const getPaymentTerm = createSelector(
  getSelectedQuote,
  (selectedQuote: SkyBasketItemQuoteDetails | undefined) =>
    selectedQuote?.paymentTerm
);

export const getSelectedContractType = createSelector(
  getSelectedQuote,
  (selectedQuote: SkyBasketItemQuoteDetails | undefined) =>
    selectedQuote?.contractTypeCode || null
);

export const getIsInsurance = createSelector(
  getSelectedQuote,
  (selectedQuote: SkyBasketItemQuoteDetails | undefined) =>
    selectedQuote?.contractTypeCode == ContractType.Insurance
);

export const getSelectedCoverType = createSelector(
  getSelectedQuote,
  (selectedQuote: SkyBasketItemQuoteDetails | undefined) =>
    selectedQuote?.coverType
);

export const getSelectedQuotePrice = createSelector(
  getSelectedQuote,
  (selectedQuote: SkyBasketItemQuoteDetails | undefined) =>
    selectedQuote?.paymentOptions[0].fee
);

export const getNumberOfFreePayments = createSelector(
  getSelectedQuote,
  (selectedQuote: SkyBasketItemQuoteDetails | undefined) =>
    selectedQuote?.paymentOptions[0].numFreePayments
);

export const getNumberOfIncludedDevices = createSelector(
  getAllQuotes,
  (quotes: SkyBasketItemQuoteDetails[] | undefined) => {
    if (quotes) {
      const arr = quotes.filter((quote, quoteIndex) =>
        quotes.some(
          (sameFeeQuote, sameFeeQuoteIndex) =>
            sameFeeQuote.paymentOptions[0]?.fee ===
              quote.paymentOptions[0]?.fee && sameFeeQuoteIndex !== quoteIndex
        )
      );

      return arr.length
        ? arr[arr.length - 1].numberOfItems
        : quotes[0].numberOfItems;
    }

    return undefined;
  }
);

export const getTotalNumberOfDevices = createSelector(
  getNumberOfIncludedDevices,
  getAllQuotes,
  (
    numberOfIncludedDevices: number | undefined,
    quotes: SkyBasketItemQuoteDetails[] | undefined
  ) =>
    quotes && numberOfIncludedDevices && quotes[quotes.length - 1].numberOfItems
);

export const getNumberOfAdditionalDevices = createSelector(
  getTotalNumberOfDevices,
  getNumberOfIncludedDevices,
  (
    totalNumberDevices: number | undefined,
    numberOfIncludedDevices: number | undefined
  ) =>
    totalNumberDevices && numberOfIncludedDevices
      ? totalNumberDevices - numberOfIncludedDevices
      : undefined
);

export const getExtraDevicePrice = createSelector(
  getAllQuotes,
  (quotes: SkyBasketItemQuoteDetails[] | undefined) => {
    return quotes && quotes.length > 1
      ? quotes &&
          quotes[quotes.length - 1].paymentOptions[0].fee -
            quotes[quotes.length - 2].paymentOptions[0].fee
      : 0;
  }
);

export const getSkyBoxPrice = createSelector(
  getAllQuotes,
  (quotes: SkyBasketItemQuoteDetails[] | undefined) =>
    quotes && quotes[0].paymentOptions[0].fee
);

export const getSkyBoxDetails = createSelector(
  getQuotesState,
  (state: QuotesState) =>
    state.quoteApiResult?.basket.items[0].data.applianceDetails
);

export const getLtPrice = createSelector(
  getSkyBoxDetails,
  (applianceDetails: SkyBasketItemApplianceDetails | undefined) =>
    applianceDetails?.ltPrice
);

export const getCheckoutBasket = createSelector(
  getSelectedQuote,
  getBasketItemQuoteApiResult,
  (
    selectedQuote: SkyBasketItemQuoteDetails | undefined,
    basketItem: SkyBasketItem | undefined
  ): CheckoutBasket | undefined => {
    if (selectedQuote && basketItem) {
      return {
        basketId: basketItem.basketId,
        items: [getCheckoutBasketItem(selectedQuote, basketItem)],
      } as CheckoutBasket;
    }

    return undefined;
  }
);

const getCheckoutBasketItem = (
  selectedQuote: SkyBasketItemQuoteDetails,
  basketItem: SkyBasketItem
): CheckoutBasketItem => {
  return {
    companyCode: selectedQuote.companyCode,
    contractType: selectedQuote.contractTypeCode,
    itemId: basketItem.itemId,
    itemType: basketItem.itemType,
    quoteId: selectedQuote.quoteId,
    schemeCode: selectedQuote.schemeCode,
    paymentOptions: convertPaymentOptionsToObject(selectedQuote.paymentOptions),
  };
};
